import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { withTheme } from 'common/styling/theme';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { staticStyles, getDynamicStyles } from './style';

const Tabs = ({ externalClass, theme, tabSlugActive, children, searchId, searchOff, displayMode }) => {
  children = useMemo(
    () => (Object.getPrototypeOf(children).constructor.name === 'Object' ? [children] : children),
    [children]
  );
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const history = useHistory();
  const location = useLocation();
  const search = queryString.parse(location.search);
  const searchTab = searchOff ? null : searchId ? search[`${searchId}_tab`] : search.tab;

  const tabs = useMemo(
    () => React.Children.toArray(children).filter(({ props: { tabTextId, tabSlug } }) => tabTextId && tabSlug),
    [children]
  );

  const updateSearch = tab => {
    if (searchOff) return;
    const propTab = searchId ? `${searchId}_tab` : 'tab';
    const fixedSearch = searchTab === tab ? search : {};
    history.replace({
      ...location,
      search: queryString.stringify({ ...fixedSearch, [propTab]: tab }),
    });
  };

  const [tabActive, setTabActive] = useState(searchTab || tabSlugActive || tabs[0].props.tabSlug);

  const tabContent = useMemo(() => {
    let content = tabs.filter(({ props: { tabSlug, tabDisabled } }) => tabSlug === tabActive && !tabDisabled)[0];
    if (!content) {
      setTabActive(children[0].props.tabSlug);
      content = children[0];
    }
    return content;
  }, [tabs, tabActive, children]);

  const tabsContent = useMemo(
    () =>
      tabs.map(item =>
        React.cloneElement(item, {
          className:
            item.props.tabSlug !== tabActive || item.props.tabDisabled
              ? `${item.props.className} ${'Tabs__content--display-none'}`
              : item.props.className,
        })
      ),
    [tabs, tabActive]
  );

  useEffect(() => updateSearch(tabActive), [tabActive]);

  return (
    <div className={classNames('Tabs', externalClass)}>
      <div className="Tabs__list">
        {tabs.map(tab => {
          const {
            key,
            props: { tabTextId, tabSlug, tabDisabled },
          } = tab;
          return (
            <button
              type="button"
              className={classNames('Tabs__tab', {
                'Tabs__tab--active': tabSlug === tabActive,
                'Tabs__tab--disabled': tabDisabled,
              })}
              disabled={tabDisabled}
              key={key}
              onClick={() => setTabActive(tabSlug)}>
              <FormattedMessage id={tabTextId} />
            </button>
          );
        })}
      </div>
      <div className="Tabs__content">{displayMode ? tabsContent : tabContent}</div>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

Tabs.propTypes = {
  externalClass: PropTypes.string,
  theme: PropTypes.object,
  tabSlugActive: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Tabs.defaultProps = {
  externalClass: '',
  tabSlugActive: null,
  theme: {},
};

export default withTheme()(Tabs);
